import { useEffect, useState } from 'react';
import { X, Plus, Building2, Check, Loader2, ArrowLeft } from 'lucide-react';
import {
  listBusinessProfiles,
  createBusinessProfile,
  type BusinessProfile,
} from '../../lib/businessProfiles';

type BusinessProfilePickerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  businessId: string;
  selectedProfileId: string | null;
  /** null = remove the assigned profile */
  onSelect: (profile: BusinessProfile | null) => void;
};

const field =
  'w-full rounded-[10px] border border-gray-300 px-3.5 py-2.5 text-[15px] text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 bg-white';
const fieldLabel = 'block text-sm font-semibold text-gray-900 mb-1.5';
const fieldHint = 'text-[12px] text-gray-500 mt-1 leading-snug';

/**
 * Pick (or create) a business profile to assign to an AI agent. List view
 * with a Create button top-right; create view is a short form describing
 * the organization.
 */
export default function BusinessProfilePickerModal({
  isOpen,
  onClose,
  businessId,
  selectedProfileId,
  onSelect,
}: BusinessProfilePickerModalProps) {
  const [view, setView] = useState<'list' | 'create'>('list');
  const [profiles, setProfiles] = useState<BusinessProfile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create form
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [about, setAbout] = useState('');
  const [servicesSummary, setServicesSummary] = useState('');
  const [serviceArea, setServiceArea] = useState('');
  const [differentiators, setDifferentiators] = useState('');
  const [website, setWebsite] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setView('list');
    setError(null);
    setLoading(true);
    listBusinessProfiles(businessId)
      .then(setProfiles)
      .catch((err) => {
        console.error('Failed to load business profiles:', err);
        setError('Could not load profiles.');
      })
      .finally(() => setLoading(false));
  }, [isOpen, businessId]);

  if (!isOpen) return null;

  const resetForm = () => {
    setName('');
    setCompanyName('');
    setAbout('');
    setServicesSummary('');
    setServiceArea('');
    setDifferentiators('');
    setWebsite('');
  };

  const handleCreate = async () => {
    if (!name.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const profile = await createBusinessProfile(businessId, {
        name: name.trim(),
        company_name: companyName.trim(),
        about: about.trim(),
        services_summary: servicesSummary.trim(),
        service_area: serviceArea.trim(),
        differentiators: differentiators.trim(),
        website: website.trim(),
      });
      resetForm();
      // Creating from this flow assigns immediately — that's why they came.
      onSelect(profile);
      onClose();
    } catch (err) {
      console.error('Failed to create business profile:', err);
      setError('Could not create the profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[85vh]"
        style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200/60 shrink-0">
          <div className="flex items-center gap-2.5 min-w-0">
            {view === 'create' && (
              <button
                onClick={() => setView('list')}
                className="p-1.5 -ml-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
            )}
            <div className="min-w-0">
              <h2 className="text-lg font-bold text-gray-900 truncate" style={{ letterSpacing: '-0.01em' }}>
                {view === 'list' ? 'Business profile' : 'New business profile'}
              </h2>
              <p className="text-sm text-gray-600 truncate" style={{ letterSpacing: '-0.01em' }}>
                {view === 'list'
                  ? 'Tell the agent what organization it\u2019s part of'
                  : 'Describe the business in your own words'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            {view === 'list' && (
              <button
                onClick={() => setView('create')}
                className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-[10px] text-sm font-semibold transition-colors"
              >
                <Plus className="h-4 w-4" />
                Create
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-4 overflow-y-auto">
          {error && (
            <p className="text-sm text-red-600 mb-3" style={{ letterSpacing: '-0.01em' }}>{error}</p>
          )}

          {view === 'list' ? (
            loading ? (
              <div className="py-10 flex justify-center">
                <Loader2 className="h-6 w-6 text-blue-600 animate-spin" />
              </div>
            ) : profiles.length === 0 ? (
              <div className="py-8 text-center">
                <div className="w-12 h-12 rounded-2xl bg-gray-100 flex items-center justify-center mx-auto mb-3">
                  <Building2 className="h-6 w-6 text-gray-400" />
                </div>
                <p className="text-[15px] font-semibold text-gray-900">No profiles yet</p>
                <p className="text-sm text-gray-500 mt-1 max-w-[280px] mx-auto">
                  Create a profile describing who you are and what you do — the agent uses it to represent your organization.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedProfileId && (
                  <button
                    onClick={() => {
                      onSelect(null);
                      onClose();
                    }}
                    className="w-full text-left p-3 rounded-[10px] border border-gray-200 hover:bg-gray-50 transition-colors text-sm font-semibold text-gray-600"
                  >
                    Remove assigned profile
                  </button>
                )}
                {profiles.map((profile) => {
                  const selected = profile.id === selectedProfileId;
                  return (
                    <button
                      key={profile.id}
                      onClick={() => {
                        onSelect(profile);
                        onClose();
                      }}
                      className={`w-full text-left p-3.5 rounded-[12px] border transition-colors flex items-start gap-3 ${
                        selected
                          ? 'border-blue-500 bg-blue-50/40'
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className={`w-9 h-9 rounded-[10px] flex items-center justify-center flex-shrink-0 ${
                        selected ? 'bg-blue-600' : 'bg-gray-200'
                      }`}>
                        {selected
                          ? <Check className="h-5 w-5 text-white" />
                          : <Building2 className="h-5 w-5 text-gray-500" />}
                      </div>
                      <div className="min-w-0">
                        <div className="text-[15px] font-semibold text-gray-900" style={{ letterSpacing: '-0.01em' }}>
                          {profile.name}
                        </div>
                        <div className="text-[13px] text-gray-500 leading-snug mt-0.5 line-clamp-2">
                          {profile.about || profile.services_summary || profile.company_name || 'No description yet.'}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )
          ) : (
            <div className="space-y-4">
              <div>
                <label className={fieldLabel}>Profile name</label>
                <input
                  className={field}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Main brand"
                />
                <p className={fieldHint}>A label for you — callers never hear this.</p>
              </div>
              <div>
                <label className={fieldLabel}>Company name</label>
                <input
                  className={field}
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="The name callers know you by"
                />
              </div>
              <div>
                <label className={fieldLabel}>Who we are</label>
                <textarea
                  className={`${field} resize-none`}
                  rows={3}
                  value={about}
                  onChange={(e) => setAbout(e.target.value)}
                  placeholder="e.g. Family-run auto detailing studio, in business since 2015. We care about doing it right, not doing it fast."
                />
              </div>
              <div>
                <label className={fieldLabel}>What we do</label>
                <textarea
                  className={`${field} resize-none`}
                  rows={3}
                  value={servicesSummary}
                  onChange={(e) => setServicesSummary(e.target.value)}
                  placeholder="e.g. Full interior and exterior detailing, ceramic coatings, and paint correction for daily drivers and exotics."
                />
              </div>
              <div>
                <label className={fieldLabel}>Where we operate</label>
                <input
                  className={field}
                  value={serviceArea}
                  onChange={(e) => setServiceArea(e.target.value)}
                  placeholder="e.g. Greater Toronto Area, mobile within 40 km"
                />
              </div>
              <div>
                <label className={fieldLabel}>What sets us apart</label>
                <textarea
                  className={`${field} resize-none`}
                  rows={2}
                  value={differentiators}
                  onChange={(e) => setDifferentiators(e.target.value)}
                  placeholder="e.g. Certified ceramic installers, 200+ five-star reviews, satisfaction guarantee."
                />
              </div>
              <div>
                <label className={fieldLabel}>Website</label>
                <input
                  className={field}
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  placeholder="https://…"
                />
              </div>
              <button
                onClick={handleCreate}
                disabled={!name.trim() || saving}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2.5 rounded-[10px] text-[15px] font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Create & assign profile
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
